import { styled } from "goober";
import { useRef, useState } from "react";
import { useMouseDownOutside } from "../hooks/useMouseDownOutside";
import { playClick } from "../lib/sounds";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";
import { Button } from "./Button";
import { Icon } from "./Icon";

const Container = styled("div")`
  position: relative;
`;

const Opener = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
`;

const Popover = styled(AnchoredPopover)`
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  z-index: ${zIndexes.dropdown};
  width: 280px;
  max-height: 360px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchContainer = styled("div")`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 12px;
  gap: 8px;
  border-bottom: 0.5px solid var(--border-alpha);
  flex-shrink: 0;
`;

const SearchInput = styled("input")`
  flex: 1;
  background: transparent;
  border: none;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  color: var(--text-primary);
  outline: none;

  &::placeholder {
    color: var(--text-primary);
    opacity: 0.3;
  }
`;

const Results = styled("div")`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 4px;
`;

export const SectionLabel = styled("div")`
  padding: 8px 8px 12px;
`;

export const Divider = styled("div")`
  height: 0.5px;
  margin: 12px 8px 4px;
  background: var(--border-alpha);
`;

const SubtleButtonElement = styled(Button)`
  &.secondary {
    color: var(--text-secondary);
  }
`;

// Default to type="button" so these never submit an enclosing modal <form>.
export function SubtleButton(props: React.ComponentProps<typeof Button>) {
  return <SubtleButtonElement type="button" {...props} />;
}

export const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: var(--alpha4);
  }

  & > .row-idle,
  & > .row-hover {
    flex-shrink: 0;
    margin-left: auto;
    margin-right: -4px;
    display: flex;
  }

  & > .row-hover {
    display: none;
  }

  &:hover > .row-idle {
    display: none;
  }

  &:hover > .row-hover {
    display: flex;
  }
`;

type Props = {
  opener: React.ReactNode;
  children: (query: string, close: () => void) => React.ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  align?: "left" | "right";
  // Gap in px between the opener and the popover (default 4).
  margin?: number;
  style?: React.CSSProperties;
  disabled?: boolean;
  // Receives an imperative open(), for external triggers like keyboard shortcuts.
  openRef?: React.MutableRefObject<(() => void) | null>;
};

export function ConnectorDropdown({
  opener,
  children,
  searchable = true,
  searchPlaceholder = "Search connectors…",
  align = "right",
  margin = 4,
  style,
  disabled,
  openRef,
}: Props) {
  const [containerRef, portalRef] = useMouseDownOutside(() => setIsOpen(false));
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef(-1);

  const close = () => setIsOpen(false);

  const open = () => {
    if (disabled) return;
    setQuery("");
    highlightRef.current = -1;
    if (searchable) requestAnimationFrame(() => inputRef.current?.focus());
    setIsOpen(true);
  };
  if (openRef) openRef.current = open;

  const toggleOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (disabled) return;
    if (isOpen) close();
    else open();
  };

  const navRows = () => [
    ...(resultsRef.current?.querySelectorAll<HTMLElement>("[data-nav-row]") ??
      []),
  ];

  const moveHighlight = (rows: HTMLElement[], index: number) => {
    highlightRef.current = index;
    rows.forEach((row, i) => {
      if (i === index) {
        row.setAttribute("data-highlighted", "");
        row.scrollIntoView({ block: "nearest" });
      } else {
        row.removeAttribute("data-highlighted");
      }
    });
  };

  // Combobox-style navigation: focus stays in the search input while arrows
  // cycle a highlight across rows opted in with data-nav-row.
  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      const rows = navRows();
      if (rows.length === 0) return;
      event.preventDefault();
      const delta = event.key === "ArrowDown" ? 1 : -1;
      const current = highlightRef.current;
      const next =
        current === -1
          ? delta === 1
            ? 0
            : rows.length - 1
          : (current + delta + rows.length) % rows.length;
      moveHighlight(rows, next);
    } else if (event.key === "Enter") {
      const row = navRows()[highlightRef.current];
      if (row) {
        event.preventDefault();
        row.click();
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      close();
    }
  };

  return (
    <Container ref={containerRef} style={style}>
      <Popover
        isOpen={isOpen}
        popoverRef={portalRef}
        direction="down"
        style={
          {
            margin,
            top: "anchor(bottom)",
            ...(align === "left"
              ? { left: "anchor(left)" }
              : { right: "anchor(right)" }),
            positionTryFallbacks:
              "flip-block, flip-inline, flip-block flip-inline",
          } as React.CSSProperties
        }
        trigger={(props) => (
          <Opener
            {...props}
            onMouseDown={(e) => {
              playClick();
              toggleOpen(e);
            }}
          >
            {opener}
          </Opener>
        )}
      >
        {searchable && (
          <SearchContainer>
            <Icon name="search" size={12} />
            <SearchInput
              ref={inputRef}
              type="text"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                moveHighlight(navRows(), -1);
              }}
              onKeyDown={handleSearchKeyDown}
            />
          </SearchContainer>
        )}
        <Results ref={resultsRef}>{children(query, close)}</Results>
      </Popover>
    </Container>
  );
}
